import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FiEdit2, FiRefreshCw, FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import { BASE_URL_API_BLOG as BASE_URL } from "../Utils/Constant";
import { basicauthblog } from "../Utils/Credentials";
import "../../assets/styles/components/_bumppy-blogs.scss";

const PAGE_LIMIT = 30;

const getValue = (blog, ...keys) => {
  const key = keys.find((item) => blog[item] !== undefined && blog[item] !== null);
  return key ? blog[key] : "";
};

const getCategoryId = (category) => category.catid ?? category.id;
const getCategoryName = (category) =>
  category.category_name || category.catname || category.name || "Unnamed";
const getCategoryRequestValue = (category) =>
  category.slug || getCategoryName(category) || String(getCategoryId(category));

const normalizeCategoryValues = (value) => {
  if (value === "" || value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(normalizeCategoryValues);
  if (typeof value === "object") {
    return normalizeCategoryValues(
      value.catid || value.category_id || value.id || value.category_name || value.name || value.slug
    );
  }

  const stringValue = String(value).trim();
  if (!stringValue) return [];

  // Supports API values such as "7, 59", "[7,59]", or a category name.
  return stringValue
    .replace(/[\[\]"]/g, "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getBlogCategoryValues = (blog) => {
  const rawCategory = getValue(
    blog,
    "catid",
    "cat_id",
    "category_id",
    "category_ids",
    "category",
    "categories",
    "post_category_id",
    "post_category"
  );

  return normalizeCategoryValues(rawCategory);
};

const resolveBlogCategories = (blog, categories) => {
  const values = getBlogCategoryValues(blog);
  const directName = getValue(blog, "category_name", "catname");
  const names = values.map((value) => {
    const normalizedValue = String(value).toLowerCase();
    const match = categories.find(
      (category) =>
        String(getCategoryId(category)) === String(value) ||
        String(category.slug).toLowerCase() === normalizedValue ||
        getCategoryName(category).toLowerCase() === normalizedValue
    );
    return match ? getCategoryName(match) : value;
  });

  if (!names.length && directName) names.push(directName);
  return [...new Set(names.map(String))];
};

const resolveBlogImage = (blog) => {
  const image = getValue(
    blog,
    "feature_url",
    "imageurl",
    "image",
    "post_featured_image",
    "featured_image",
    "featuredImage",
    "thumbnail",
    "thumbnail_url",
    "post_image"
  );

  if (typeof image === "string") return image.trim();
  if (image && typeof image === "object") {
    return String(
      image.url || image.src || image.source_url || image.image_url || ""
    ).trim();
  }
  return "";
};

const BumppyBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");
  const pageScrollRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const getHeaders = useCallback(() => {
    const { password, username } = basicauthblog;
    const base64Credentials = btoa(`${username}:${password}`);

    return {
      Authorization: `Basic ${base64Credentials}`,
      "Content-Type": "application/json",
    };
  }, []);

  const fetchBlogs = useCallback(async (categorySlug, requestedPage = 1) => {
    const isFirstPage = requestedPage === 1;
    if (isFirstPage) {
      setIsLoading(true);
      setError("");
    } else {
      loadingMoreRef.current = true;
      setIsLoadingMore(true);
    }

    try {
      const blogsResponse = await fetch(`${BASE_URL}/GetAllBlog`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          page: requestedPage,
          limit: PAGE_LIMIT,
          category: categorySlug,
          language: "en",
        }),
      });
      const blogsResult = await blogsResponse.json();

      if (!blogsResponse.ok) {
        throw new Error("The Bumppy blog service could not be reached.");
      }

      if (blogsResult.status !== "0") {
        throw new Error(blogsResult.msg || "Unable to load Bumppy blogs.");
      }

      const nextBlogs = Array.isArray(blogsResult.data) ? blogsResult.data : [];
      setBlogs((currentBlogs) =>
        isFirstPage ? nextBlogs : [...currentBlogs, ...nextBlogs]
      );
      setPage(requestedPage);
      setHasMore(nextBlogs.length === PAGE_LIMIT);
    } catch (requestError) {
      if (isFirstPage) {
        setError(requestError.message || "Something went wrong while loading blogs.");
        setBlogs([]);
      }
    } finally {
      if (isFirstPage) setIsLoading(false);
      else {
        loadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [getHeaders]);

  const loadBumppyBlogs = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const categoriesResponse = await fetch(`${BASE_URL}/GetBlogCategories`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          type : "0"
        }),
      });
      const categoriesResult = await categoriesResponse.json();

      if (!categoriesResponse.ok || String(categoriesResult.status) !== "0") {
        throw new Error(categoriesResult.msg || "Unable to load categories.");
      }

      const categoryList = Array.isArray(categoriesResult.data)
        ? categoriesResult.data
            .map((category) => ({
              id: String(category.id),
              category_name: String(category.category_name || "").trim(),
            }))
            .filter((category) => category.id && category.category_name)
        : [];
      setCategories(categoryList);

      const firstCategory = categoryList[0];
      if (!firstCategory) {
        setSelectedCategory("");
        setBlogs([]);
        setIsLoading(false);
        return;
      }

      const categoryValue = getCategoryRequestValue(firstCategory);
      setSelectedCategory(categoryValue);
      await fetchBlogs(categoryValue);
    } catch (requestError) {
      setError(requestError.message || "Something went wrong while loading categories.");
      setBlogs([]);
      setIsLoading(false);
    }
  }, [fetchBlogs, getHeaders]);

  const handleCategoryChange = (category) => {
    const categoryValue = getCategoryRequestValue(category);
    setSelectedCategory(categoryValue);
    setSearch("");
    setPage(1);
    setHasMore(true);
    if (pageScrollRef.current) pageScrollRef.current.scrollTop = 0;
    fetchBlogs(categoryValue, 1);
  };

  const refreshBlogs = () => {
    if (selectedCategory) {
      setPage(1);
      setHasMore(true);
      fetchBlogs(selectedCategory, 1);
    }
    else loadBumppyBlogs();
  };

  const handleTableScroll = (event) => {
    const element = event.currentTarget;
    const isNearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    if (isNearBottom && hasMore && !isLoading && !loadingMoreRef.current && selectedCategory) {
      fetchBlogs(selectedCategory, page + 1);
    }
  };

  useEffect(() => {
    loadBumppyBlogs();
  }, [loadBumppyBlogs]);

  const filteredBlogs = useMemo(() => {
    const query = search.trim().toLowerCase();

    return blogs.filter((blog) => {
      const title = String(getValue(blog, "title", "post_title")).toLowerCase();
      const author = String(
        getValue(blog, "author_name", "post_author", "author")
      ).toLowerCase();
      return !query || title.includes(query) || author.includes(query);
    });
  }, [blogs, search]);

  return (
    <main
      className="bumppy-blogs-page"
      ref={pageScrollRef}
      onScroll={handleTableScroll}
    >
      <header className="bumppy-page-header">
        <div>
          <h2>Bumppy Blogs</h2>
       </div>
        <button type="button" className="refresh-button" onClick={refreshBlogs}>
          <FiRefreshCw /> Refresh
        </button>
      </header>

      <nav className="category-tabs" aria-label="Blog categories">
        {categories.map((category) => {
          const categoryValue = getCategoryRequestValue(category);
          return (
            <button
              type="button"
              key={getCategoryId(category)}
              className={selectedCategory === categoryValue ? "active" : ""}
              onClick={() => handleCategoryChange(category)}
            >
              {getCategoryName(category)}
            </button>
          );
        })}
        <span className="category-end-spacer" aria-hidden="true" />
      </nav>

      <section className="bumppy-toolbar">
        <label className="blog-search">
          <FiSearch />
          <input
            type="search"
            placeholder="Search title or author"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <span className="result-count">{filteredBlogs.length} articles</span>
      </section>

      <section className="bumppy-table-card">
        {isLoading ? (
          <div className="bumppy-state"><Spinner animation="border" /> Loading blogs...</div>
        ) : error ? (
          <div className="bumppy-state error-state">
            <strong>Unable to load blogs</strong>
            <span>{error}</span>
            <button type="button" onClick={refreshBlogs}>Try again</button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="bumppy-blog-table">
              <colgroup>
                <col className="number-column" />
                <col className="article-column" />
                <col className="category-column" />
                <col className="author-column" />
                <col className="status-column" />
                <col className="action-column" />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Article</th>
                  <th>Category</th>
                  <th className="action-heading">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlogs.length ? filteredBlogs.map((blog, index) => {
                  const title = getValue(blog, "title", "post_title") || "Untitled article";
                  const slug = getValue(blog, "slug", "post_slug", "post_name");
                  const image = resolveBlogImage(blog);
                  const blogCategories = resolveBlogCategories(blog, categories);
                  const author = getValue(blog, "author_name", "post_author", "author") || "—";
                  const status = getValue(blog, "status", "post_status") || "Published";

                  return (
                    <tr key={getValue(blog, "id", "post_id", "tempid") || index}>
                      <td className="row-number">{index + 1}</td>
                      <td>
                        <a
                          className="article-cell article-link"
                          href={`https://bumppy.com/${String(slug).replace(/^\/+/, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Open ${title} on Bumppy`}
                        >
                          {image ? (
                            <img
                              src={image}
                              alt={getValue(blog, "image_alt", "post_featured_image_alt") || title}
                              loading="lazy"
                            />
                          ) : (
                            <span className="image-placeholder">B</span>
                          )}
                          <div>
                            <strong>{title}</strong>
                            <span>{slug}</span>
                          </div>
                        </a>
                      </td>
                      <td>
                        {blogCategories.length ? (
                          blogCategories.map((category) => (
                            <span className="category-chip" key={category}>{category}</span>
                          ))
                        ) : (
                          <span className="category-chip">Uncategorized</span>
                        )}
                      </td>
                    <td className="action-cell">
                        <Link
                          className="edit-blog-button"
                          to="/blog/BumppyEditBlog"
                          title={`Edit ${title}`}
                          aria-label={`Edit ${title}`}
                          onClick={() =>
                            sessionStorage.setItem("Editblogid", JSON.stringify(blog))
                          }
                        >
                          <FiEdit2 />
                        </Link>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="6" className="empty-row">No matching blogs found.</td></tr>
                )}
              </tbody>
            </table>
            {isLoadingMore && (
              <div className="load-more-state">
                <Spinner animation="border" size="sm" /> Loading more blogs...
              </div>
            )}
            {!hasMore && blogs.length > 0 && (
              <div className="end-of-results">You’ve reached the end of the list.</div>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default BumppyBlogs;
